import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDashboard } from "@fortawesome/free-solid-svg-icons/faDashboard";
import { faBook, faBoxesPacking, faCog, faLandmark, faLocation, faUser, faUsers, faChalkboardTeacher, faDumbbell, faUserClock, faMoneyBill, faCashRegister, faHistory, faChartBar, faUserShield, faUserGear, faBuilding } from "@fortawesome/free-solid-svg-icons";
import {
    isMemberRole,
    isDynamicStaffRole,
    hasScreen,
    hasCompanyFeature,
    isPlatformSuperAdmin,
    canReadScreen,
} from "../utils/roleAccess";

/**
 * SuperAdmin is a platform console — gym screens stay hidden.
 * Company feature flags hide modules even when the role would otherwise see them.
 */
function hideGymItem(role, user, screenKey, mode = "clubAndAdmin") {
    if (isPlatformSuperAdmin(role) || isMemberRole(role)) return true;
    if (!hasCompanyFeature(user, screenKey)) return true;

    if (mode === "allStaff") {
        if (isDynamicStaffRole(role, user) && !hasScreen(user, screenKey)) return true;
        return false;
    }
    if (mode === "adminOnly") {
        if (role === "Admin") return false;
        if (isDynamicStaffRole(role, user) && hasScreen(user, screenKey)) return false;
        return true;
    }
    if (role === "Admin" || role === "ClubAdmin") return false;
    if (isDynamicStaffRole(role, user) && hasScreen(user, screenKey)) return false;
    return true;
}

const SideMenu = (role, user = null) => [
    {
        url: '/dashboard',
        title: 'Dashboard',
        icon: <FontAwesomeIcon icon={faDashboard} />,
        ignore:
            isMemberRole(role) ||
            (isDynamicStaffRole(role, user) &&
                (!hasScreen(user, "dashboard") || !canReadScreen(user, "dashboard"))),
    },
    {
        url: '/member/dashboard',
        title: 'Dashboard',
        icon: <FontAwesomeIcon icon={faDashboard} />,
        ignore: !isMemberRole(role),
    },
    {
        url: '/member/invoices',
        title: 'My Invoices',
        icon: <FontAwesomeIcon icon={faMoneyBill} />,
        ignore: !isMemberRole(role),
    },
    {
        url: '/member/profile',
        title: 'My Profile',
        icon: <FontAwesomeIcon icon={faUser} />,
        ignore: !isMemberRole(role),
    },
    {
        url: '/companies',
        title: 'Companies',
        icon: <FontAwesomeIcon icon={faBuilding} />,
        ignore: !isPlatformSuperAdmin(role),
    },
    {
        url: '/locations',
        title: 'Locations',
        icon: <FontAwesomeIcon icon={faLocation} />,
        ignore: hideGymItem(role, user, 'locations', 'adminOnly'),
    },
    {
        url: '/members',
        title: 'Members',
        icon: <FontAwesomeIcon icon={faUser} />,
        ignore: hideGymItem(role, user, 'members'),
    },
    {
        url: '/ledgers',
        title: 'Subscriptions',
        icon: <FontAwesomeIcon icon={faBook} />,
        ignore: hideGymItem(role, user, 'subscriptions', 'allStaff'),
    },
    {
        url: '/facilities',
        title: 'Facilities',
        icon: <FontAwesomeIcon icon={faLandmark} />,
        ignore: hideGymItem(role, user, 'facilities'),
    },
    {
        url: '/packages',
        title: 'Packages',
        icon: <FontAwesomeIcon icon={faBoxesPacking} />,
        ignore: hideGymItem(role, user, 'packages'),
    },
    {
        url: `/company-ledgers`,
        title: 'Company Ledgers',
        icon: <FontAwesomeIcon icon={faBook} />,
        ignore: hideGymItem(role, user, 'company_ledgers'),
    },
    {
        url: `/expense`,
        title: 'Expense',
        icon: <FontAwesomeIcon icon={faMoneyBill} />,
        ignore: hideGymItem(role, user, 'expense'),
    },
    {
        url: '/pos',
        title: 'Point of Sale',
        icon: <FontAwesomeIcon icon={faCashRegister} />,
        ignore: hideGymItem(role, user, 'pos'),
    },
    {
        url: '/trainers',
        title: 'Trainers',
        icon: <FontAwesomeIcon icon={faChalkboardTeacher} />,
        ignore: hideGymItem(role, user, 'trainers'),
    },
    {
        url: '/assets',
        title: 'Assets',
        icon: <FontAwesomeIcon icon={faDumbbell} />,
        ignore: hideGymItem(role, user, 'assets'),
    },
    {
        url: '/staffmember',
        title: 'Staff Member',
        icon: <FontAwesomeIcon icon={faUsers} />,
        ignore: hideGymItem(role, user, 'staff_members'),
    },
    {
        url: '/staffattendance',
        title: 'Staff Attendance',
        icon: <FontAwesomeIcon icon={faUserClock} />,
        ignore: hideGymItem(role, user, 'staff_attendance'),
    },
    {
        url: '/visitor',
        title: 'Visitor',
        icon: <FontAwesomeIcon icon={faUser} />,
        ignore: hideGymItem(role, user, 'visitors'),
    },
    {
        title: 'Reporting',
        icon: <FontAwesomeIcon icon={faBook} />,
        ignore: hideGymItem(role, user, 'reporting'),
        children: [
            {
                url: '/member-status-audit',
                title: 'Member Audit',
                icon: <FontAwesomeIcon icon={faHistory} />,
            },
            {
                url: '/income-breakdown-report',
                title: 'Income Breakdown',
                icon: <FontAwesomeIcon icon={faMoneyBill} />,
            },
            {
                url: '/member-trainer-log-report',
                title: 'Trainer Assignments',
                icon: <FontAwesomeIcon icon={faUserClock} />,
            },
            {
                url: '/membership-analysis-report',
                title: 'Membership Analysis',
                icon: <FontAwesomeIcon icon={faChartBar} />,
            },
            {
                url: '/personal-coaching-analysis-report',
                title: 'Coaching Analysis',
                icon: <FontAwesomeIcon icon={faChalkboardTeacher} />,
            },
        ]
    },
    {
        url: '/admin/users',
        title: 'Users',
        icon: <FontAwesomeIcon icon={faUserShield} />,
        ignore: isPlatformSuperAdmin(role)
            ? false
            : hideGymItem(role, user, 'admin_users', 'adminOnly'),
    },
    {
        url: '/admin/roles',
        title: 'Roles',
        icon: <FontAwesomeIcon icon={faUserGear} />,
        ignore: isPlatformSuperAdmin(role)
            ? false
            : hideGymItem(role, user, 'admin_roles', 'adminOnly'),
    },
    {
        url: '/settings',
        title: 'Settings',
        icon: <FontAwesomeIcon icon={faCog} />,
        ignore: isPlatformSuperAdmin(role) || hideGymItem(role, user, 'settings', 'adminOnly'),
    },
]

export default SideMenu
